import React, { useRef, useState } from 'react';

import a11yKeyPress from 'js/lib/a11yKeyPress';
import useRouter from 'js/lib/useRouter';

import MiniProfileRenderer from 'bundles/classmates/components/MiniProfileRenderer';
import { getProfileLink } from 'bundles/discussions/utils/discussionsUrl';

import 'css!./__styles__/ProfileName';

type ProfileNameProps = {
  fullName: string;
  externalId: string;
  ariaHidden?: boolean;
  ariaLabel?: string;
};

const ProfileName = ({ fullName, externalId, ariaHidden = false, ariaLabel }: ProfileNameProps) => {
  const router = useRouter();
  const [showMini, setShowMini] = useState(false);
  const miniProfileRef = useRef<HTMLSpanElement | null>(null);

  const onNameClick = () => {
    router.push(getProfileLink(externalId));
  };

  /**
   * onMouseLeave takes effect when user slantly moves the cursor from fullName display,
   * trying to access the mini profile.
   * This prevents the mini profile from disappearing while user is trying to interact with it.
   */
  const handleMouseLeave = () => {
    setTimeout(() => {
      const miniProfileElement = miniProfileRef.current;
      if (miniProfileElement && miniProfileElement.matches(':hover')) {
        return;
      }
      setShowMini(false);
    }, 2000);
  };

  return (
    <span
      tabIndex={ariaHidden ? -1 : 0}
      role="link"
      className="rc-ProfileName nostyle pii-hide"
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      onClick={onNameClick}
      onKeyPress={(event) => a11yKeyPress(event, onNameClick)}
      onMouseEnter={() => setShowMini(true)}
      onFocus={() => setShowMini(true)}
      onMouseLeave={() => handleMouseLeave()}
      onBlur={() => setShowMini(false)}
    >
      <span className="profile-name">{fullName}</span>
      <span data-js="mini-profile" style={{ position: 'absolute', zIndex: 99 }} ref={miniProfileRef}>
        <MiniProfileRenderer source="profileName" externalId={externalId} showMini={showMini} fullName={fullName} />
      </span>
    </span>
  );
};

export default ProfileName;
